import { useCallback } from "react";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import {
  useExpanded,
  usePagination,
  useRowSelect,
  useSortBy,
  useTable,
} from "react-table";
import { useSticky } from "react-table-sticky";
import { useLocalStorage } from "react-use";

import { CaseDocumentWithPresignedUrl } from "pwapi";

import ApplicationState from "store/ApplicationState";

import AdditionalDocumentsTable from "./AdditionalDocumentsTable";
import ReportFiltersForm from "./ReportFiltersForm";
import ReportListColumns from "./ReportListColumns";
import ReportListTableView from "./ReportListTableView";
import ListLayout from "../ListLayout";
import ListPagination from "../ListPagination";

type ReportListProps = {
  columns: any;
  reportType: "requisition" | "report";
};

export const getAdditionalDocuments = (
  documents: any[],
  reportType: "requisition" | "report"
): any[] =>
  documents.filter((doc: any) =>
    reportType === "report"
      ? doc.category !== CaseDocumentWithPresignedUrl.category.CASE_REPORT
      : doc.category !== CaseDocumentWithPresignedUrl.category.REQUISITION_FORM
  ) || [];

const ReportList = ({ columns, reportType }: ReportListProps) => {
  const [search] = useSearchParams();

  const {
    results: reports,
    count: totalRows,
    loading,
  } = useSelector(({ documentsList }: ApplicationState) => documentsList);

  const [reportPageSize, setReportPageSize] = useLocalStorage<number>(
    "reportListDefaultPageSize"
  );

  const {
    headerGroups,
    rows,
    prepareRow,
    toggleAllRowsExpanded,

    // Pagination
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,

    state: { sortBy, pageIndex, pageSize },
  } = useTable(
    {
      columns,
      data: reports,
      autoResetExpanded: false,
      manualSortBy: true,
      manualPagination: true,
      pageCount: -1,
      disableSortRemove: true,
      initialState: {
        pageIndex:
          search.get("page") && parseInt(`${search.get("page")}`)
            ? parseInt(`${search.get("page")}`) - 1
            : 0,
        pageSize: reportPageSize || 10,
        sortBy: [
          {
            desc: false,
            id: "collected_date",
          },
        ],
      },
    },

    useSortBy,
    useExpanded,
    usePagination,
    useRowSelect,
    useSticky
  );

  const pageCount = totalRows ? Math.ceil(totalRows / pageSize) : -1;

  const handleChangeReportSize = (newValue: string) => {
    setReportPageSize(parseInt(newValue));
  };

  const renderRowSubComponent = useCallback(
    ({ row }: any) => {
      const additionalDocs = getAdditionalDocuments(
        row.original.case_document_views,
        reportType
      ).map((doc: any) => ({
        ...doc,
        case: { id: row.original.id, accession: row.original.accession },
        patient: row.original.patient,
      }));

      return <AdditionalDocumentsTable documents={additionalDocs} />;
    },
    [reportType]
  );

  return (
    <ListLayout
      isListLoading={loading}
      resourceCount={reports?.length}
      navigation={
        <h1>
          Case {reportType === "report" ? "Reports" : "Requisition Forms"}
        </h1>
      }
      filters={
        <ReportFiltersForm
          sortBy={sortBy}
          setPage={gotoPage}
          pageIndex={pageIndex}
          pageSize={pageSize}
          listLoading={loading}
          totalCount={totalRows}
          reportType={reportType}
          toggleAllRowsExpanded={toggleAllRowsExpanded}
        />
      }
      table={
        <ReportListTableView
          headerGroups={headerGroups}
          rows={rows}
          prepareRow={prepareRow}
          reportType={reportType}
          renderRowSubComponent={renderRowSubComponent}
        />
      }
      pagination={
        <ListPagination
          canPreviousPage={pageIndex !== 0}
          canNextPage={pageIndex < pageCount - 1}
          gotoPage={gotoPage}
          nextPage={nextPage}
          previousPage={previousPage}
          pageSizes={[10, 25]}
          setPageSize={setPageSize}
          pageIndex={pageIndex}
          pageSize={pageSize}
          pageCount={pageCount}
          totalCount={totalRows}
          toggleAllRowsExpanded={toggleAllRowsExpanded}
          setToLocalStorage={handleChangeReportSize}
        />
      }
    />
  );
};

export default ReportListColumns(ReportList);
